import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useCart } from '../contexts/CartContext';

export default function VendorPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [items, setItems] = useState([]);
  const [operatingHours, setOperatingHours] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch vendor info
        const vendorRes = await api(`/api/vendors/${id}`, {});
        if (!vendorRes.ok) {
          const data = await vendorRes.json().catch(() => ({}));
          throw new Error(data.error || `Error ${vendorRes.status}`);
        }
        const vendorData = await vendorRes.json();
        
        // Fetch vendor items (stock > 0 only)
        const itemsRes = await api(`/api/items/vendor/${id}`, {});
        if (!itemsRes.ok) {
          const data = await itemsRes.json().catch(() => ({}));
          throw new Error(data.error || `Error ${itemsRes.status}`);
        }
        const itemsData = await itemsRes.json();

        // Fetch operating hours
        const hoursRes = await api(`/api/garage-sellers/${id}/operating-hours`, {});
        const hoursData = hoursRes.ok ? await hoursRes.json() : [];

        if (!cancelled) {
          setVendor(vendorData);
          setItems(itemsData);
          setOperatingHours(hoursData);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleItemClick = (itemId) => {
    navigate(`/item/${itemId}`);
  };

  const handleAddToCart = (e, itemId, isOutOfStock) => {
    e.stopPropagation();
    if (!isOutOfStock) {
      addItem(itemId, 1);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading garage sale page…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Garage Sale</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Garage Sale</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          Garage sale not found.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Hero Section with Shopping Bag Icons - Home Page Style */}
      <div className="relative overflow-hidden rounded-xl bg-[#a9d2f2] shadow-sm">
        {/* Pink accent lines at top and bottom */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-pink-300"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-pink-300"></div>
        
        {/* Decorative circular shapes */}
        <div className="absolute -left-12 top-1/2 -translate-y-1/2 w-32 h-32 bg-blue-300/40 rounded-full"></div>
        <div className="absolute -right-12 top-1/2 -translate-y-1/2 w-32 h-32 bg-blue-300/40 rounded-full"></div>
        
        {/* Content */}
        <div className="relative px-6 py-8 md:py-10 text-center">
          {/* Shopping Bag Icons */}
          <div className="relative flex items-center justify-center mb-4 h-20">
            {/* Orange bag (background, positioned behind and to the right) */}
            <div className="absolute z-0" style={{ transform: 'translateX(20px) translateY(6px)' }}>
              <svg width="56" height="64" viewBox="0 0 56 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8C10.9 8 10 8.9 10 10V12H8C6.9 12 6 12.9 6 14V54C6 55.1 6.9 56 8 56H48C49.1 56 50 55.1 50 54V14C50 12.9 49.1 12 48 12H46V10C46 8.9 45.1 8 44 8H12ZM14 10H42V12H14V10ZM8 14H48V54H8V14Z" fill="#F97316"/>
                <rect x="16" y="20" width="24" height="2" fill="#F97316"/>
                <rect x="16" y="26" width="24" height="2" fill="#F97316"/>
                <rect x="16" y="32" width="24" height="2" fill="#F97316"/>
              </svg>
            </div>
            {/* Blue bag (foreground, larger) */}
            <div className="relative z-10">
              <svg width="64" height="72" viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 8C12.9 8 12 8.9 12 10V12H10C8.9 12 8 12.9 8 14V58C8 59.1 8.9 60 10 60H54C55.1 60 56 59.1 56 58V14C56 12.9 55.1 12 54 12H52V10C52 8.9 51.1 8 50 8H14ZM16 10H48V12H16V10ZM10 14H54V58H10V14Z" fill="#3B82F6"/>
                <rect x="18" y="22" width="28" height="2.5" fill="#3B82F6"/>
                <rect x="18" y="29" width="28" height="2.5" fill="#3B82F6"/>
                <rect x="18" y="36" width="28" height="2.5" fill="#3B82F6"/>
              </svg>
            </div>
          </div>
          
          {/* Garage Sale Name */}
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            {vendor.storeName ? `${vendor.storeName} Garage sale` : 'Unnamed Store'}
          </h1>
        </div>
      </div>

      {/* Garage Sale Details */}
      <div className="flex flex-col gap-4">
        {(vendor.marketName || vendor.address) && (
          <div className="flex flex-col gap-2">
            {vendor.marketName && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 dark:text-slate-400">City:</span>
                <span className="text-lg font-medium">{vendor.marketName}</span>
              </div>
            )}
            {(vendor.address || vendor.marketName) && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address || vendor.marketName)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm inline-block mt-1"
              >
                Get Directions →
              </a>
            )}
          </div>
        )}

        {/* Operating Hours */}
        {operatingHours.length > 0 && (
          <div className="flex flex-col gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold">Operating Hours</h3>
            <div className="flex flex-col gap-2">
              {operatingHours.map((hours, idx) => {
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const formatTime = (timeStr) => {
                  if (!timeStr) return '';
                  const [hours, minutes] = timeStr.split(':');
                  const hour = parseInt(hours);
                  const ampm = hour >= 12 ? 'PM' : 'AM';
                  const displayHour = hour % 12 || 12;
                  return `${displayHour}:${minutes} ${ampm}`;
                };
                const formatDate = (dateStr) => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  return date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                };
                return (
                  <div key={hours.id || idx} className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium">
                      {hours.saleDate ? formatDate(hours.saleDate) : (hours.dayOfWeek !== null && hours.dayOfWeek !== undefined ? dayNames[hours.dayOfWeek] : 'Date')}
                    </span>
                    {hours.startTime && hours.endTime ? (
                      <span>: {formatTime(hours.startTime)} - {formatTime(hours.endTime)}</span>
                    ) : hours.startTime ? (
                      <span>: Starts at {formatTime(hours.startTime)}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Phone */}
        {vendor.phone && (
          <div className="flex items-center gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <span className="text-slate-500 dark:text-slate-400" aria-hidden>📞</span>
            <span className="text-lg">{vendor.phone}</span>
          </div>
        )}
      </div>

      {/* Search Bar - filters to this garage seller only */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const query = e.target.querySelector('input').value.trim();
          if (query) {
            navigate(`/search?q=${encodeURIComponent(query)}&garageSellerId=${id}`);
          }
        }}
        className="w-full"
      >
        <div className="relative">
          <input
            type="text"
            placeholder="Search items in this garage sale..."
            className="w-full h-12 text-base px-4 pr-14 rounded-full border-0 bg-gray-100 text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          <button
            type="submit"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors flex items-center justify-center"
            aria-label="Search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </div>
      </form>

      {/* Items Grid */}
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold">Items</h2>
        
        {items.length === 0 ? (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
            <p className="text-xl text-slate-600 dark:text-slate-400">
              No items available at this time.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-0">
            {items.map((item) => {
              const imageUrls = item.imageUrls || [];
              const firstImage = imageUrls.length > 0 ? imageUrls[0] : null;
              const isOutOfStock = item.stockCount <= 0;
              const hasLowStock = item.stockCount > 0 && item.stockCount < 10;

              return (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item.id)}
                  className="relative w-full bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  {/* Amazon-style horizontal card layout */}
                  <div className="flex gap-3 p-3">
                    {/* Left: Image - Only show first image, no carousel controls */}
                    <div className="relative flex-shrink-0 w-32 h-32 bg-slate-100 dark:bg-slate-900 rounded">
                      {firstImage ? (
                        <>
                          <img
                            src={firstImage}
                            alt={item.title}
                            className="w-full h-full object-contain rounded"
                          />
                          {/* Overlay icon (like Amazon's share/list icon) */}
                          <div className="absolute bottom-1 left-1 w-6 h-6 bg-white/90 rounded flex items-center justify-center">
                            <svg className="w-4 h-4 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                          No image
                        </div>
                      )}
                    </div>

                    {/* Right: Details */}
                    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                      {/* Title */}
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-tight">
                        {item.title}
                      </h3>

                      {/* Rating (placeholder - you can add actual ratings later) */}
                      <div className="flex items-center gap-1">
                        <div className="flex items-center">
                          {Array.from({ length: 5 }, (_, i) => (
                            <span
                              key={i}
                              className={`text-xs ${i < 4 ? 'text-orange-400' : 'text-slate-300 dark:text-slate-600'}`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-slate-600 dark:text-slate-400">(0)</span>
                      </div>

                      {/* Price */}
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                          ${(parseFloat(item.price) || 0).toFixed(2).split('.')[0]}
                        </span>
                        <span className="text-xs text-slate-600 dark:text-slate-400 align-super">
                          {(parseFloat(item.price) || 0).toFixed(2).split('.')[1]}
                        </span>
                      </div>

                      {/* Stock Warning */}
                      {hasLowStock && !isOutOfStock && (
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                          Only {item.stockCount} left in stock - order soon.
                        </p>
                      )}
                      {isOutOfStock && (
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                          Out of stock
                        </p>
                      )}

                      {/* Add to Cart Button */}
                      <button
                        type="button"
                        onClick={(e) => handleAddToCart(e, item.id, isOutOfStock)}
                        disabled={isOutOfStock}
                        className="mt-1 w-full h-9 rounded-md bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors font-semibold text-sm text-slate-900 flex items-center justify-center"
                      >
                        {isOutOfStock ? 'Out of Stock' : 'Add to cart'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
